import React, { useMemo, useContext } from 'react';
import { IDropdownProps } from '../../@types/dropdown';
import * as input from '../../components/connectedForm/inputComponents';
import EditableDataGrid from '../../components/EditableDataGrid/EditableDataGrid';
import { cloneDeep } from '@apollo/client/utilities';
import { useQuery } from '@apollo/client';
import ProductsWarehouse from '../../graphql/entities/product/ProductsWarehouse';
import { IEditableDataGridColumnDef } from '../../@types/editableDataGridTypes';
import { useDispatch } from 'react-redux';
import { default as Form } from '../../components/connectedForm';
import {
  FilterOperation,
  IProductsWarehouseQueryResult,
  IProductsWarehouseQueryVariables,
  ISupplier,
} from '../../graphql/schema/p4types';
import ConnectedFormContext from '../../components/Context/ConnectedFormContext';
import Formatters from '../../components/grid-addons/formatters';

const ATTRIBUTE_COLUMNS: Array<IEditableDataGridColumnDef> = [
  {
    name: 'Attribute Name',
    key: 'name',
    editable: false,
  },
  {
    name: 'Attribute Value',
    key: 'value',
    editable: true,
  },
  {
    name: 'Required',
    key: 'required',
    formatter: Formatters.BooleanFormatter,
    editable: false,
    width: 120,
  },
];

type IPurchaseEdiConfigurationPropsType = {
  supplier: ISupplier;
};

export const PurchaseEdiConfiguration = (props: IPurchaseEdiConfigurationPropsType) => {
  const { supplier } = props;
  const dispatch = useDispatch();
  const { change } = useContext(ConnectedFormContext);

  const { data: warehouses } = useQuery<IProductsWarehouseQueryResult, IProductsWarehouseQueryVariables>(
    ProductsWarehouse.dropdown.query
  );

  const warehouseColumns = useMemo(() => {
    if (warehouses) {
      const columns = [
        {
          name: 'Attribute',
          key: 'attributeConfig.name',
          editable: false,
        },
      ];
      return [
        ...columns,
        ...warehouses.list.edges.map((warehouse: any) => {
          return {
            key: `warehouse${warehouse.node.id}`,
            name: warehouse.node.description,
            editable: (rowData) =>
              rowData?.attributeConfig?.name || (rowData?.attributeConfig?.name as string)?.length > 0,
          } as IEditableDataGridColumnDef;
        }),
        {
          name: 'Required',
          key: 'attributeConfig.required',
          formatter: Formatters.BooleanFormatter,
          editable: false,
          width: 120,
        },
      ];
    }
  }, [warehouses]);

  const attributeConfigs = useMemo(() => {
    return cloneDeep(supplier?.purchaseEdiConfiguration?.ediAttributesConfigs);
  }, [supplier?.purchaseEdiConfiguration]);

  const warehouseAttributesConfigs = useMemo(() => {
    return cloneDeep(supplier?.purchaseEdiConfiguration?.warehouseAttributesConfigs);
  }, [supplier?.purchaseEdiConfiguration]);

  const attributeData = useMemo(() => {
    if (attributeConfigs) {
      return attributeConfigs.map((attributeConfig: any) => {
        const attributeValue = supplier?.purchaseEdiAttributeValues.find(
          (av: any) => av?.config?.id === attributeConfig.id
        );
        let value;
        if (attributeValue?.value) {
          value = attributeValue.value;
        }

        if (!attributeValue?.value && !attributeConfig.required) {
          value = attributeConfig?.defaultValue ?? '';
        }

        return {
          name: attributeConfig.attributeName,
          value: value,
          required: attributeConfig.required,
          config: attributeConfig,
        };
      });
    }
    return [];
  }, [supplier?.purchaseEdiAttributeValues, attributeConfigs]);

  const warehouseAttributeData = useMemo(() => {
    if (warehouseAttributesConfigs) {
      return warehouseAttributesConfigs.map((wac: any) => {
        const line = { attributeConfig: { ...wac } };
        warehouses?.list.edges.forEach((warehouse: any) => {
          const warehouseValue = supplier.purchaseEdiWarehouseAttributeValues.find(
            (wav: any) => wav?.warehouse?.id === warehouse.node.id && wav?.attributeConfig?.id === wac?.id
          );
          line[`warehouse${warehouse.node.id}`] = warehouseValue?.value ?? wac.defaultValue;
        });
        return line;
      });
    }
    return [];
  }, [supplier?.purchaseEdiWarehouseAttributeValues, warehouses, warehouseAttributesConfigs]);

  const updatedEdiAttributeValue = (value: string, rowIdx: number) => {
    const line = attributeData[rowIdx];
    line.value = value;

    const existingValues = [...(supplier?.purchaseEdiAttributeValues ?? [])];

    const attributeIndex = existingValues.findIndex((existingValue) => existingValue.config.id === line.config.id);

    if (attributeIndex === -1) {
      const newAttributeValue = {
        value: value,
        config: line.config,
        supplier: {
          id: supplier.id,
        },
      };
      existingValues.push(newAttributeValue);
    } else {
      existingValues[attributeIndex] = {
        ...existingValues[attributeIndex],
        value: value,
      };
    }
    dispatch(change('purchaseEdiAttributeValues', existingValues));
  };

  const updateWarehouseValue = (warehouseId: number, value: string, rowIdx: number) => {
    // does a value exist for the warehouse
    if (warehouseAttributeData.length > rowIdx) {
      // we are editing an existing value
      const line = warehouseAttributeData[rowIdx];

      const existingValues = [...(supplier?.purchaseEdiWarehouseAttributeValues ?? [])];
      // do we have a value set for it already?
      const warehouseAttributeValue = existingValues.find((attributeVal) => attributeVal.warehouse.id === warehouseId);

      if (warehouseAttributeValue) {
        warehouseAttributeValue.value = value;
      } else {
        const newValue = {
          supplier: {
            id: supplier?.id,
          },
          warehouse: { id: warehouseId },
          value: value,
          attributeConfig: line.attributeConfig,
        };
        existingValues.push(newValue);
      }
      dispatch(change('purchaseEdiWarehouseAttributeValues', existingValues));
    }
  };

  return (
    <>
      <Form.Legend label="Purchase EDI" />
      <Form.Field<IDropdownProps>
        dataid="purchaseEdiConfiguration"
        label="Purchase Edi Configuration"
        component={input.Dropdown}
        predefinedFilters={[
          {
            filterOperation: FilterOperation.Equals,
            filterParameters: {
              field: 'enabled',
              value: 'true',
            },
          },
        ]}
      />
      {attributeConfigs?.length > 0 || warehouseAttributeData?.length > 0 ? (
        <Form.TabPanel>
          <Form.TabContent
            id="ediAttributes"
            label="Attributes"
            key="ediAttributes"
            shouldRender={attributeConfigs && attributeConfigs.length > 0}
          >
            <Form.Legend label={'Edi Attributes'} />
            <EditableDataGrid
              columns={ATTRIBUTE_COLUMNS}
              listData={attributeData}
              enableCellSelect={true}
              onRowUpdated={(commit) => {
                updatedEdiAttributeValue(commit?.updated?.value, commit.rowIdx);
              }}
            />
          </Form.TabContent>
          <Form.TabContent
            id="warehouseEdiAttributes"
            label="Warehouse"
            key="warehouseEdiAttributes"
            shouldRender={warehouseAttributesConfigs && warehouseAttributesConfigs.length}
          >
            <Form.Legend label={'Warehouse Edi Attributes'} />
            <EditableDataGrid
              columns={warehouseColumns}
              listData={warehouseAttributeData}
              enableCellSelect={true}
              onRowUpdated={(commit) => {
                if ((commit.cellKey as string).startsWith('warehouse')) {
                  //update the value
                  const value = commit.updated[commit.cellKey] as string;
                  const warehouseId = (commit.cellKey as string).replace('warehouse', '');
                  updateWarehouseValue(parseInt(warehouseId, 10), value, commit.rowIdx);
                }
              }}
            />
          </Form.TabContent>
        </Form.TabPanel>
      ) : null}
    </>
  );
};
